/**
 * @fileoverview Landing page month strip component with static display.
 * Simplified version of MonthStrip for showcasing the UI.
 * @module features/landing/components/LandingMonthStrip
 */

import { type ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { useIsMobile } from '@/shared/hooks/useIsMobile'

/**
 * Props for LandingMonthStrip component
 */
interface LandingMonthStripProps {
  className?: string
}

/** Month keys for translation */
const MONTH_KEYS = ['jul', 'aug', 'sep', 'oct', 'nov', 'dec'] as const

/** Currently selected month index (December = last in array) */
const SELECTED_MONTH = 5

/** Display year */
const YEAR = 2025

/**
 * Landing page month strip component.
 * Displays a static month selector UI without functionality.
 *
 * @component
 */
export function LandingMonthStrip({ className = '' }: LandingMonthStripProps): ReactElement {
  const { t } = useTranslation('features-landing')
  const isMobile = useIsMobile()

  // On mobile, show only 3 months ending with the selected month
  const { visibleMonths, selectedIdx } = useMemo(() => {
    if (isMobile) {
      // Show last 3 months (Oct-Dec) to include selected month (December)
      const startIdx = Math.max(0, SELECTED_MONTH - 2)
      const months = MONTH_KEYS.slice(startIdx, startIdx + 3)
      return {
        visibleMonths: months,
        selectedIdx: SELECTED_MONTH - startIdx,
      }
    }
    return {
      visibleMonths: MONTH_KEYS,
      selectedIdx: SELECTED_MONTH,
    }
  }, [isMobile])

  return (
    <div className={`pb-4 ${className}`}>
      <div className="min-w-0 overflow-x-hidden w-full">
        <ButtonGroup className="w-full items-center">
          <Button
            aria-label={t('demo.monthStrip.previousMonth')}
            variant="outline"
            size="lg"
            className="border border-border shrink-0 w-11 px-0 cursor-default"
          >
            <ChevronLeft className="w-6 h-6 text-muted-foreground" />
          </Button>

          {visibleMonths.map((monthKey, idx) => {
            const label = t(`demo.months.${monthKey}`)
            const active = idx === selectedIdx
            const isFutureMonth = idx > selectedIdx
            const buttonClassName = `border text-base flex-1 min-w-0 transition-all cursor-default ${
              active
                ? 'bg-muted-selector text-foreground !h-11 self-center z-10 shadow-sm ring-1 ring-inset ring-white/15 before:absolute before:inset-0 before:bg-white/[0.03] before:pointer-events-none'
                : 'border-border text-muted-foreground'
            } ${isFutureMonth ? 'opacity-60' : ''}`

            return (
              <Button
                key={`${monthKey}-${idx}`}
                type="button"
                size="lg"
                variant="outline"
                aria-pressed={active}
                aria-current={active ? 'true' : undefined}
                className={`relative overflow-hidden rounded-none ${buttonClassName}`}
              >
                {isFutureMonth ? (
                  <span className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-card/30 to-transparent" />
                ) : null}
                <span className="inline-flex items-baseline gap-2 relative">
                  <span>{label}</span>
                  {active ? <span className="text-sm opacity-70">{YEAR}</span> : null}
                </span>
              </Button>
            )
          })}

          <Button
            aria-label={t('demo.monthStrip.nextMonth')}
            variant="outline"
            size="lg"
            className="border border-border shrink-0 w-11 px-0 cursor-default"
            disabled
          >
            <ChevronRight className="w-6 h-6 text-muted-foreground" />
          </Button>
        </ButtonGroup>
      </div>
    </div>
  )
}
